import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * CONFY Badge Design System
 *
 * Variants:
 * - default: Gray background - neutral information
 * - success: Green background - positive states (Paid, Active, Completed)
 * - warning: Amber background - caution states (Pending, Draft)
 * - error: Red background - negative states (Cancelled, Failed, Overdue)
 * - info: Blue background - informational (New, Featured, Courses)
 * - purple: Purple background - events, special categories
 * - urgent: Orange background - urgent items (approaching deadline)
 *
 * Sizes:
 * - sm: Compact badges for tables and lists
 * - default: Standard badge size
 * - lg: Larger badges for emphasis
 *
 * Shape:
 * - default: Rounded corners (rounded-md)
 * - pill: Fully rounded (rounded-full)
 *
 * Accessibility Notes:
 * - All variants meet WCAG AA contrast requirements (4.5:1+)
 * - Text is always readable against background
 */
const badgeVariants = cva(
  // Base styles - all badges
  "inline-flex items-center gap-1 font-medium whitespace-nowrap",
  {
    variants: {
      variant: {
        // Default: Gray - neutral information
        default: "bg-gray-100 text-gray-700",

        // Success: Green - positive states (WCAG AA: 4.6:1)
        success: "bg-green-100 text-green-700",

        // Warning: Amber - caution states (WCAG AA: 4.5:1)
        warning: "bg-amber-100 text-amber-700",

        // Error: Red - negative states (WCAG AA: 4.7:1)
        error: "bg-red-100 text-red-700",

        // Info: Blue - informational (WCAG AA: 4.5:1)
        info: "bg-blue-100 text-blue-700",

        // Purple: Purple - events, special categories (WCAG AA: 4.5:1)
        purple: "bg-purple-100 text-purple-700",

        // Urgent: Orange - approaching deadline (WCAG AA: 4.5:1)
        urgent: "bg-orange-100 text-orange-700",
      },
      size: {
        // Small: For tables, compact lists
        sm: "px-1.5 py-0.5 text-xs",

        // Default: Standard badge
        default: "px-2 py-0.5 text-xs",

        // Large: Emphasized badges
        lg: "px-2.5 py-1 text-sm",
      },
      shape: {
        // Default: Rounded corners
        default: "rounded-md",

        // Pill: Fully rounded
        pill: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Optional icon to display before the text */
  icon?: React.ReactNode
}

function Badge({
  className,
  variant,
  size,
  shape,
  icon,
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      data-slot="badge"
      className={cn(badgeVariants({ variant, size, shape, className }))}
      {...props}
    >
      {icon && <span className="shrink-0 [&_svg]:size-3">{icon}</span>}
      {children}
    </span>
  )
}

export { Badge, badgeVariants }
